/**
 * OnlineExamApp — online-exam recording rig with live on-screen gaze.
 * Enter via `?view=onlineexam`.
 *
 * Reuses the existing BlazeGaze wiring (useWebEyeTracker → normPog, centered
 * [-0.5,0.5]) + camera, renders a moving-content mock exam (instruction fixed
 * on top; question + answers at randomized screen positions each item), and
 * runs the tested ScreenGaze module live: which region is looked at, in what
 * order, what was never looked at, and the sustained-off-content-fixation cue
 * (phone-on-screen). Records the webcam + downloads a JSON log for analysis.
 *
 * Purpose: the CEO directive (2026-07-08) — set up the cheating environment,
 * record, and find the signal. Everything is real-time (not post-processing).
 * Observation only — never a verdict.
 *
 * NOTE: camera + BlazeGaze + calibration behavior must be verified live in a
 * browser (localhost/https, camera permission). This file is compile-verified.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useCamera } from '../hooks/useCamera';
import { useWebEyeTracker } from '../hooks/useWebEyeTracker';
import { matchScreenGaze, ScreenGazeSession, type ScreenRegion, type ScreenGazeReport } from '../engine/ScreenGaze';
import { useHandLandmarker } from '../hooks/useHandLandmarker';

const VIDEO_ID = 'webcamVideo';
const NQ = 10;

// ── Replay override (test without a live camera) ──────────────────────────
// `?view=onlineexam&replay=1` → feed a recorded video (public/replay.mp4, or
// ?replay=<url>) into BlazeGaze by intercepting getUserMedia with a
// captureStream from a hidden looping <video>. Validates the gaze PLUMBING on
// an existing recording; it does NOT validate the online cheat signal (those
// demos are paper/offline, so gaze won't align with on-screen content).
(function installReplayIfRequested() {
  if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;
  const r = new URLSearchParams(window.location.search).get('replay');
  if (!r || !window.location.search.includes('view=onlineexam')) return;
  const url = r === '1' ? '/replay.mp4' : r;
  let streamP: Promise<MediaStream> | null = null;
  const getReplayStream = () => {
    if (streamP) return streamP;
    streamP = new Promise<MediaStream>((resolve, reject) => {
      const v = document.createElement('video');
      v.src = url; v.loop = true; v.muted = true; (v as any).playsInline = true;
      v.oncanplay = () => v.play()
        .then(() => resolve(((v as any).captureStream?.() ?? (v as any).mozCaptureStream?.()) as MediaStream))
        .catch(reject);
      v.onerror = () => reject(new Error('replay video load failed: ' + url));
    });
    return streamP;
  };
  const orig = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
  navigator.mediaDevices.getUserMedia = ((c: MediaStreamConstraints) =>
    c && (c as any).video ? getReplayStream() : orig(c)) as typeof navigator.mediaDevices.getUserMedia;
  // eslint-disable-next-line no-console
  console.log('[OnlineExam] replay override active →', url);
})();
const CALIB_N = 3; // 3x3 grid

// ── Random-probe cheat cue (Kay 2026-07-10: don't chase gaze accuracy; read
// behavior). A phone propped in a non-content screen zone OCCLUDES it and the
// gaze estimate can't localize "looking at a physical phone in front of the
// screen" (confirmed on the 07-10 Massa recording — the blue dot stayed in the
// content area, spread too wide). So instead: periodically render an
// interactive probe in a NON-content zone (esp. where a phone would sit —
// right / lower, from the video). To answer it the examinee must move the
// phone → typing stops (a hand leaves the keyboard). We catch the BEHAVIOR:
//   probe missed/slow  AND/OR  keyboard paused around the probe.
// Gaze is a secondary signal only. Observation, never a verdict.
// (Hand-landmark reach — the strongest of Kay's cues — needs MediaPipe Hands,
// not wired in this BlazeGaze-only view; tracked as a follow-up.)
const PROBE_FIRST_MS = 6000;
const PROBE_GAP = [7000, 13000] as const; // random interval between probes
const PROBE_TIMEOUT_MS = 3500;            // no response within this → missed
const PROBE_KB_PAUSE_MS = 1500;           // typing gap that counts as "stopped"
const PROBE_ZONES = [
  { zone: 'right', x: [0.80, 0.92], y: [0.30, 0.58] },        // where the phone sat (video)
  { zone: 'lower-right', x: [0.72, 0.90], y: [0.72, 0.88] },
  { zone: 'lower', x: [0.30, 0.66], y: [0.85, 0.92] },
  { zone: 'upper-right', x: [0.78, 0.92], y: [0.12, 0.26] },  // phone can sit higher
] as const;

// Hand-reach cue (Kay's strongest signal): to reach a phone the hand rises.
// Detected relative to the hand's own rolling resting baseline (mirror- and
// framing-robust — we only use vertical rise). Gated by ?hands=off.
const HAND_CANVAS_ID = 'oe_hand_overlay';
const REACH_DY = 0.12;          // hand rose ≥12% of frame above its baseline = reach
const REACH_BASE_ALPHA = 0.03;  // baseline EMA (updated only while resting)

interface Probe { id: string; zone: string; rect: { x: number; y: number; w: number; h: number }; shownAt: number; q: number; }
interface ProbeRec { id: string; zone: string; tShown: number; respondedMs: number | null; missed: boolean; sinceLastKeyMs: number; gazeVisited: boolean; handReach: boolean; q: number; }

type Phase = 'idle' | 'calib' | 'exam' | 'done';
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const rnd = (a: number, b: number) => a + Math.random() * (b - a);

interface QSpec { instruction: ScreenRegion; question: ScreenRegion; answer: ScreenRegion; prompt: string; }

const PROMPTS = [
  '17 × 23 = ?', '한 변이 8cm인 정사각형의 넓이는?', '대한민국의 광복절은? (MM/DD)',
  '"물은 100℃에서 ___한다" 빈칸은?', '144의 제곱근은?', '일주일은 몇 시간인가?',
  '"Apple"의 복수형은?', '3의 5제곱은?', '삼각형 내각의 합은? (도)', '이순신 장군의 대표 해전은?',
];

function makeQuestion(i: number): QSpec {
  // instruction fixed at top (so "instruction never read" is detectable)
  const instruction: ScreenRegion = { id: `instr_${i}`, role: 'instruction', rect: { x: 0.08, y: 0.04, w: 0.84, h: 0.07 } };
  // question (upper) + answer input (lower) BOTH move every item → the answer
  // area is a moving target, so a gaze fixed off it has no content reason.
  const question: ScreenRegion = { id: `q_${i}`, role: 'question', rect: { x: rnd(0.08, 0.40), y: rnd(0.15, 0.30), w: 0.52, h: 0.16 } };
  const answer: ScreenRegion = { id: `a_${i}`, role: 'answer', rect: { x: rnd(0.10, 0.42), y: rnd(0.54, 0.76), w: 0.48, h: 0.15 } };
  return { instruction, question, answer, prompt: PROMPTS[i % PROMPTS.length] };
}

const pct = (v: number) => `${(v * 100).toFixed(2)}%`;
function boxStyle(r: ScreenRegion): React.CSSProperties {
  return { position: 'absolute', left: pct(r.rect.x), top: pct(r.rect.y), width: pct(r.rect.w), height: pct(r.rect.h) };
}

export default function OnlineExamApp() {
  useCamera(VIDEO_ID);
  const eye = useWebEyeTracker(VIDEO_ID);
  const handsEnabled = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('hands') !== 'off';
  const hand = useHandLandmarker(VIDEO_ID, HAND_CANVAS_ID, handsEnabled);
  const [phase, setPhase] = useState<Phase>('idle');
  const [qi, setQi] = useState(0);
  const [calibIdx, setCalibIdx] = useState(0);
  const [hud, setHud] = useState<{ region: string; entropy: number; fixation: boolean; glances: number; reach: boolean }>({ region: '-', entropy: 0, fixation: false, glances: 0, reach: false });
  const [report, setReport] = useState<ScreenGazeReport | null>(null);
  const [ans, setAns] = useState('');
  const [kb, setKb] = useState<{ keys: number; dwell: number; flight: number }>({ keys: 0, dwell: 0, flight: 0 });
  const [probeUi, setProbeUi] = useState<Probe | null>(null);
  const [probeStat, setProbeStat] = useState<{ shown: number; missed: number; slow: number }>({ shown: 0, missed: 0, slow: 0 });

  const sessionRef = useRef(new ScreenGazeSession({ offContentMinMs: 4000, fixationRadius: 0.06 }));
  const specRef = useRef<QSpec[]>(Array.from({ length: NQ }, (_, i) => makeQuestion(i)));
  const gazeLogRef = useRef<Array<{ t: number; x: number; y: number; region: string; q: number }>>([]);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const t0Ref = useRef(0);
  // keyboard dynamics (a DrSimon channel): timing only, key IDENTITY masked to a
  // category (char/space/back/enter) — neutral, no keylogging.
  const keyLogRef = useRef<Array<{ t: number; ev: 'down' | 'up'; cat: string; q: number }>>([]);
  const downAtRef = useRef<Record<string, number>>({});
  const lastDownRef = useRef(0);
  const dwellsRef = useRef<number[]>([]);
  const flightsRef = useRef<number[]>([]);
  // random-probe cheat cue
  const probeRef = useRef<Probe | null>(null);
  const probeGazeSeenRef = useRef(false);
  const nextProbeAtRef = useRef(0);
  const probeSeqRef = useRef(0);
  const probeLogRef = useRef<ProbeRec[]>([]);
  // hand-reach tracking
  const handRef = useRef<{ detected: boolean; y: number | null }>({ detected: false, y: null });
  const handBaseYRef = useRef<number | null>(null);
  const handReachSeenRef = useRef(false);

  const currentRegions = (): ScreenRegion[] => {
    if (phase !== 'exam') return [];
    const s = specRef.current[qi];
    return [s.instruction, s.question, s.answer];
  };

  const catOf = (k: string) => (k === ' ' ? 'space' : k === 'Backspace' ? 'back' : k === 'Enter' ? 'enter' : k.length === 1 ? 'char' : 'other');
  const mean = (a: number[]) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : 0);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (phase !== 'exam') return;
    const now = performance.now() - t0Ref.current;
    downAtRef.current[e.key] = now;
    if (lastDownRef.current) flightsRef.current.push(now - lastDownRef.current);
    lastDownRef.current = now;
    keyLogRef.current.push({ t: Math.round(now), ev: 'down', cat: catOf(e.key), q: qi + 1 });
    if (e.key === 'Enter') { e.preventDefault(); next(); }
  };
  const onKeyUp = (e: React.KeyboardEvent) => {
    if (phase !== 'exam') return;
    const now = performance.now() - t0Ref.current;
    const d = downAtRef.current[e.key];
    if (d != null) dwellsRef.current.push(now - d);
    keyLogRef.current.push({ t: Math.round(now), ev: 'up', cat: catOf(e.key), q: qi + 1 });
    setKb({ keys: keyLogRef.current.filter((k) => k.ev === 'down').length, dwell: Math.round(mean(dwellsRef.current)), flight: Math.round(mean(flightsRef.current)) });
  };

  // keep latest hand center in a ref (the rAF tick's closure would be stale)
  useEffect(() => {
    handRef.current = { detected: hand.detected, y: hand.center ? hand.center.y : null };
  }, [hand.detected, hand.center]);

  const spawnProbe = (now: number) => {
    const z = PROBE_ZONES[probeSeqRef.current % PROBE_ZONES.length];
    probeSeqRef.current += 1;
    const rect = { x: rnd(z.x[0], z.x[1]), y: rnd(z.y[0], z.y[1]), w: 0.06, h: 0.05 };
    const p: Probe = { id: `probe_${probeSeqRef.current}`, zone: z.zone, rect, shownAt: now, q: qi + 1 };
    probeRef.current = p; probeGazeSeenRef.current = false; handReachSeenRef.current = false;
    setProbeUi(p);
    setProbeStat((s) => ({ ...s, shown: s.shown + 1 }));
  };
  const closeProbe = (now: number, respondedMs: number | null) => {
    const p = probeRef.current; if (!p) return;
    const sinceLastKeyMs = lastDownRef.current ? Math.round(now - lastDownRef.current) : PROBE_TIMEOUT_MS;
    const missed = respondedMs == null;
    const slow = respondedMs != null && respondedMs > 1500;
    probeLogRef.current.push({
      id: p.id, zone: p.zone, tShown: Math.round(p.shownAt),
      respondedMs: respondedMs == null ? null : Math.round(respondedMs),
      missed, sinceLastKeyMs, gazeVisited: probeGazeSeenRef.current,
      handReach: handReachSeenRef.current, q: p.q,
    });
    setProbeStat((s) => ({ shown: s.shown, missed: s.missed + (missed ? 1 : 0), slow: s.slow + (slow ? 1 : 0) }));
    probeRef.current = null; setProbeUi(null);
    nextProbeAtRef.current = now + rnd(PROBE_GAP[0], PROBE_GAP[1]);
  };
  const resolveProbe = () => {
    const now = performance.now() - t0Ref.current;
    if (probeRef.current) closeProbe(now, now - probeRef.current.shownAt);
  };

  // live gaze loop
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (phase !== 'exam') return;
      const g = eye.gazeData;
      const now = performance.now() - t0Ref.current;
      const regions = currentRegions();
      let pt: { x: number; y: number } | null = null;
      if (g && g.gazeState === 'open') {
        pt = { x: clamp01(g.normPog[0] + 0.5), y: clamp01(g.normPog[1] + 0.5) };
      }
      sessionRef.current.update(now, pt, regions);
      // hand-reach: hand rose above its own rolling resting baseline
      let reachActive = false;
      const h = handRef.current;
      if (h.detected && h.y != null) {
        if (handBaseYRef.current == null) handBaseYRef.current = h.y;
        else {
          reachActive = (handBaseYRef.current - h.y) > REACH_DY;
          // update the resting baseline only when NOT reaching (so a sustained
          // reach doesn't drag the baseline up and hide itself)
          if (!reachActive) handBaseYRef.current += (h.y - handBaseYRef.current) * REACH_BASE_ALPHA;
        }
      }
      // random-probe lifecycle (behavioral cheat cue, gaze-independent)
      if (!probeRef.current && now >= nextProbeAtRef.current) {
        spawnProbe(now);
      } else if (probeRef.current) {
        const p = probeRef.current;
        if (pt && pt.x >= p.rect.x && pt.x <= p.rect.x + p.rect.w && pt.y >= p.rect.y && pt.y <= p.rect.y + p.rect.h) {
          probeGazeSeenRef.current = true;
        }
        if (reachActive) handReachSeenRef.current = true;
        if (now - p.shownAt > PROBE_TIMEOUT_MS) closeProbe(now, null);
      }
      if (pt) {
        const m = matchScreenGaze(pt, regions);
        gazeLogRef.current.push({ t: Math.round(now), x: +pt.x.toFixed(4), y: +pt.y.toFixed(4), region: m.top.id, q: qi + 1 });
        const rep = sessionRef.current.report();
        setHud({ region: `${m.top.role}${m.onContent ? '' : ' (off)'}`, entropy: +m.entropy.toFixed(2), fixation: rep.sustainedOffContentFixation.active, glances: rep.repeatedOffContentGlances.count, reach: reachActive });
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, qi, eye.gazeData]);

  const startRecording = async () => {
    try {
      const v = document.getElementById(VIDEO_ID) as HTMLVideoElement | null;
      let stream = (v?.srcObject as MediaStream | null) ?? null;
      // Fallback: if BlazeGaze/useCamera didn't attach a stream, grab the camera directly.
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        if (v) { v.srcObject = stream; await v.play().catch(() => {}); }
      }
      if (!stream) return;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream, { mimeType: 'video/webm' });
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.start(1000);
      recRef.current = rec;
    } catch { /* recording optional */ }
  };
  const stopRecording = () => {
    const rec = recRef.current;
    if (!rec) return;
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `onlineexam_webcam_${Date.now()}.webm`; a.click();
    };
    rec.stop();
  };

  const beginCalib = () => { eye.startCalibration(); setCalibIdx(0); setPhase('calib'); };
  const calibClick = () => {
    const col = calibIdx % CALIB_N, row = Math.floor(calibIdx / CALIB_N);
    eye.handleCalibClick(((col + 0.5) / CALIB_N) - 0.5, ((row + 0.5) / CALIB_N) - 0.5);
    if (calibIdx + 1 >= CALIB_N * CALIB_N) { eye.endCalibration(); beginExam(); }
    else setCalibIdx(calibIdx + 1);
  };
  const beginExam = async () => {
    sessionRef.current.reset(); gazeLogRef.current = []; t0Ref.current = performance.now();
    keyLogRef.current = []; dwellsRef.current = []; flightsRef.current = []; lastDownRef.current = 0;
    probeRef.current = null; probeLogRef.current = []; probeSeqRef.current = 0; probeGazeSeenRef.current = false;
    handReachSeenRef.current = false; handBaseYRef.current = null;
    nextProbeAtRef.current = PROBE_FIRST_MS; setProbeUi(null); setProbeStat({ shown: 0, missed: 0, slow: 0 });
    setKb({ keys: 0, dwell: 0, flight: 0 }); setAns('');
    await startRecording(); setQi(0); setPhase('exam');
  };
  const next = () => {
    setAns(''); lastDownRef.current = 0;
    if (qi + 1 >= NQ) { finish(); return; }
    setQi(qi + 1);
  };
  const finish = () => {
    stopRecording();
    setReport(sessionRef.current.report());
    setPhase('done');
  };
  // combined behavioral cue: a probe not answered normally (missed/slow) AND
  // typing had stopped around it (a hand left the keyboard — consistent with
  // moving a phone). This is the gaze-INDEPENDENT signal (Kay 2026-07-10).
  const probeCue = () => {
    const ps = probeLogRef.current;
    const abnormal = (p: ProbeRec) => p.missed || (p.respondedMs != null && p.respondedMs > 1500);
    // behavioral corroboration: typing stopped OR hand reached, around the probe
    const hits = ps.filter((p) => abnormal(p) && (p.sinceLastKeyMs >= PROBE_KB_PAUSE_MS || p.handReach));
    return {
      total: ps.length,
      missed: ps.filter((p) => p.missed).length,
      behavioralHits: hits.length,
      withHandReach: ps.filter((p) => abnormal(p) && p.handReach).length,
    };
  };
  const downloadLog = () => {
    const data = {
      schema: 'onlineexam_rig_v2',
      questions: specRef.current.map((s, i) => ({ q: i + 1, prompt: s.prompt, regions: [s.instruction, s.question, s.answer] })),
      gaze: gazeLogRef.current,
      keyboard: { events: keyLogRef.current, meanDwellMs: Math.round(mean(dwellsRef.current)), meanFlightMs: Math.round(mean(flightsRef.current)), keystrokes: keyLogRef.current.filter((k) => k.ev === 'down').length },
      // random-probe behavioral cue (gaze-independent phone-on-screen signal)
      probes: probeLogRef.current,
      probeCue: probeCue(),
      report: sessionRef.current.report(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `onlineexam_log_${Date.now()}.json`; a.click();
  };

  const g = eye.gazeData;
  const dot = g && g.gazeState === 'open'
    ? { left: pct(clamp01(g.normPog[0] + 0.5)), top: pct(clamp01(g.normPog[1] + 0.5)) } : null;

  return (
    <div style={{ position: 'fixed', inset: 0, fontFamily: "'Malgun Gothic',system-ui,sans-serif", overflow: 'hidden', background: '#fff', color: '#1b2230' }}>
      <video id={VIDEO_ID} autoPlay playsInline muted
        style={{ position: 'fixed', right: 8, bottom: 8, width: 160, height: 120, objectFit: 'cover', transform: 'scaleX(-1)', border: '2px solid #335b8f', borderRadius: 8, zIndex: 40 }} />
      {/* offscreen canvas the hand-landmarker draws into (we only use the data) */}
      <canvas id={HAND_CANVAS_ID} style={{ display: 'none' }} />
      {/* status */}
      <div style={{ position: 'fixed', left: 8, top: 8, zIndex: 50, fontSize: 12, background: '#fff', border: '1px solid #dfe3ea', borderRadius: 8, padding: '6px 10px' }}>
        BlazeGaze: {eye.isReady ? 'ready' : eye.error ? `err: ${eye.error}` : 'loading…'} · 보는 곳: <b>{hud.region}</b> · H {hud.entropy}
        <span style={{ color: '#335b8f' }}> · ⌨ {kb.keys}타 dwell {kb.dwell}ms flight {kb.flight}ms</span>
        {hud.fixation && <span style={{ color: '#c0392b', fontWeight: 700 }}> · 콘텐츠 밖 고정</span>}
        {hud.glances >= 3 && <span style={{ color: '#c0392b', fontWeight: 700 }}> · 같은 밖 지점 반복 응시 ×{hud.glances}</span>}
        <span style={{ color: '#9a741e' }}> · 확인지점 {probeStat.shown}개(무응답 {probeStat.missed}·지연 {probeStat.slow})</span>
        {probeUi && <span style={{ color: '#c0392b', fontWeight: 700 }}> · 확인지점 대기중</span>}
        {handsEnabled && <span style={{ color: hand.detected ? '#2e6b53' : '#9aa0aa' }}> · ✋{hand.detected ? (hud.reach ? ' 손 리치' : ' 감지') : ' 없음'}</span>}
      </div>
      {/* live gaze dot — shown in ALL phases so BlazeGaze readiness is visible the
          moment it loads (it loads a few seconds late: CDN wasm + model). */}
      {dot && <div style={{ position: 'fixed', ...dot, width: 18, height: 18, marginLeft: -9, marginTop: -9, borderRadius: '50%', background: 'rgba(46,91,255,.45)', border: '2px solid #2e5bff', boxShadow: '0 0 0 4px rgba(46,91,255,.15)', zIndex: 60, pointerEvents: 'none' }} />}

      {/* random probe in a non-content zone — clicking dismisses it. If a phone
          occludes that zone the examinee can't see/click it (→ missed), and to
          reach it they move the phone (→ typing pauses). Behavioral cheat cue. */}
      {phase === 'exam' && probeUi && (
        <button onClick={resolveProbe} aria-label="확인 지점"
          style={{ position: 'absolute', left: pct(probeUi.rect.x), top: pct(probeUi.rect.y), width: pct(probeUi.rect.w), height: pct(probeUi.rect.h), zIndex: 58, background: '#fff7e6', border: '2px solid #f59e0b', borderRadius: 8, color: '#9a741e', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          확인 ✓
        </button>
      )}

      {phase === 'idle' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, textAlign: 'center', padding: 24 }}>
          <h1>온라인 시험 리그 (BlazeGaze)</h1>
          <p style={{ color: '#535966', maxWidth: 620, lineHeight: 1.6 }}>
            카메라를 허용하고 잠시 기다리면 <b style={{ color: '#2e5bff' }}>파란 점</b>이 화면에 떠서 시선을 따라 움직입니다
            (BlazeGaze 로딩에 몇 초 걸립니다). 점이 뜨면 준비 완료 — 캘리브레이션(9점 응시+클릭) 후 모의 시험을 봅니다.
            문항과 <b>답안 입력칸이 매 문항 다른 위치</b>에 뜨고, 답은 <b>키보드로 입력</b>합니다(시선 + 타이핑 동시 관측).
            화면 밖 같은 지점을 반복해서 응시하면 그 패턴이 기록·표시됩니다 (관찰 기록이며 판정이 아닙니다).
            (녹화는 [시작] 시 자동 — 종료 시 webm + 로그 JSON 다운로드.)
          </p>
          <button onClick={eye.isReady ? beginCalib : beginExam}
            style={{ background: '#2e5bff', color: '#fff', border: 0, borderRadius: 8, padding: '10px 18px', fontSize: 15, cursor: 'pointer' }}>
            {eye.isReady ? '캘리브레이션 시작' : 'BlazeGaze 없이 녹화 시작 (시선은 오프라인 분석)'}
          </button>
          <p style={{ margin: 0, fontSize: 12, color: eye.isReady ? '#2e6b53' : '#9a741e' }}>
            BlazeGaze: {eye.isReady ? 'ready' : eye.error ? `오류: ${eye.error}` : '로딩 중… (안 떠도 아래 버튼으로 녹화는 가능 — 웹캠+콘텐츠 로그 저장, 시선은 녹화본으로 분석)'}
          </p>
        </div>
      )}

      {phase === 'calib' && (() => {
        const col = calibIdx % CALIB_N, row = Math.floor(calibIdx / CALIB_N);
        const left = ((col + 0.5) / CALIB_N) * 100, top = ((row + 0.5) / CALIB_N) * 100;
        return (
          <div style={{ position: 'absolute', inset: 0, background: '#0f1420', zIndex: 55 }}>
            <div style={{ position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13 }}>
              점을 응시하며 클릭하세요 ({calibIdx + 1}/{CALIB_N * CALIB_N})
            </div>
            <button onClick={calibClick} aria-label="calibration point"
              style={{ position: 'absolute', left: `${left}%`, top: `${top}%`, transform: 'translate(-50%,-50%)', width: 34, height: 34, borderRadius: '50%', background: '#ff3b30', border: '4px solid #fff', cursor: 'pointer' }} />
          </div>
        );
      })()}

      {phase === 'exam' && (() => {
        const s = specRef.current[qi];
        return (
          <div style={{ position: 'absolute', inset: 0 }}>
            <div style={{ ...boxStyle(s.instruction), border: '1px solid #dfe3ea', borderRadius: 8, padding: 8, fontSize: 13, color: '#535966', background: '#f7f9fc' }}>
              [지시문] 다음 문항을 읽고 정답을 고르세요. (문항 {qi + 1}/{NQ})
            </div>
            <div style={{ ...boxStyle(s.question), border: '1px solid #dfe3ea', borderRadius: 10, padding: 12, background: '#fff', fontSize: 16, fontWeight: 700 }}>
              문항 {qi + 1}. {s.prompt}
            </div>
            {/* moving answer INPUT — typed, so the keyboard-dynamics channel runs */}
            <div style={{ ...boxStyle(s.answer), border: '1.5px solid #2e5bff', borderRadius: 10, padding: 10, background: '#f7f9ff', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#335b8f', fontWeight: 700 }}>답안 입력 (Enter = 다음)</span>
              <input key={qi} autoFocus value={ans}
                onChange={(e) => setAns(e.target.value)} onKeyDown={onKeyDown} onKeyUp={onKeyUp}
                placeholder="정답을 입력하세요"
                style={{ fontSize: 16, padding: '8px 10px', border: '1px solid #b9c6e0', borderRadius: 8, outline: 'none' }} />
              <button onClick={next} style={{ alignSelf: 'flex-end', background: '#2e5bff', color: '#fff', border: 0, borderRadius: 8, padding: '6px 14px', fontSize: 14, cursor: 'pointer' }}>다음 ▶</button>
            </div>
          </div>
        );
      })()}

      {phase === 'done' && report && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, textAlign: 'center' }}>
          <h1>완료</h1>
          <div style={{ fontSize: 14, color: '#1b2230', maxWidth: 640, textAlign: 'left', lineHeight: 1.7 }}>
            <div>· 콘텐츠 밖 시선 비율: <b>{(report.offContentRatio * 100).toFixed(1)}%</b></div>
            {/* neutral observation wording only — no verdict terms (6원칙) */}
            <div>· 콘텐츠 밖 지속 고정: <b style={{ color: report.sustainedOffContentFixation.active ? '#c0392b' : '#2e6b53' }}>{report.sustainedOffContentFixation.active ? `관찰됨 (${report.sustainedOffContentFixation.ms}ms)` : '없음'}</b></div>
            <div>· 같은 화면-밖 지점 반복 응시: <b style={{ color: report.repeatedOffContentGlances.count >= 3 ? '#c0392b' : '#2e6b53' }}>{report.repeatedOffContentGlances.count >= 3 ? `${report.repeatedOffContentGlances.count}회 (해당 지점 ${report.repeatedOffContentGlances.point ? `${(report.repeatedOffContentGlances.point.x * 100).toFixed(0)},${(report.repeatedOffContentGlances.point.y * 100).toFixed(0)}` : '-'})` : `${report.repeatedOffContentGlances.count}회`}</b></div>
            <div>· 한 번도 안 본 영역: <b>{report.notLookedAt.map((r) => r.role).join(', ') || '없음'}</b></div>
            <div>· 첫 응시 순서: {report.order.slice(0, 8).join(' → ')}</div>
            {/* gaze-independent behavioral cue (random probe + typing pause) */}
            <div>· 확인지점 반응(무응답/지연 + 입력 정지 또는 손 리치 동반): <b style={{ color: probeCue().behavioralHits > 0 ? '#c0392b' : '#2e6b53' }}>{probeCue().behavioralHits}회 / 총 {probeCue().total}개 (무응답 {probeCue().missed}, 손 리치 동반 {probeCue().withHandReach})</b></div>
          </div>
          <button onClick={downloadLog} style={{ background: '#2e5bff', color: '#fff', border: 0, borderRadius: 8, padding: '10px 18px', fontSize: 15, cursor: 'pointer' }}>로그 저장 (JSON)</button>
        </div>
      )}
    </div>
  );
}
